import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/notifications/notification.service';
import { SessionService } from 'src/app/session.service';
import { UiService } from 'src/app/ui/ui.service';
import { pageSizeOptions } from 'src/app/shared/utils/pagination';
import { IntegrationsService } from '../../integrations/integrations.service';
import { LocalStorageService } from 'src/app/storage/local-storage.service';
import { UtilService } from 'src/app/util/util.service';
import moment from 'moment-timezone';

@Component({
  selector: 'app-thread-list',
  templateUrl: './thread-list.component.html',
  styleUrls: ['./thread-list.component.scss']
})
export class ThreadListComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  filterForm!: FormGroup;
  displayedColumns = [
    'created_at',
    'user_name',
    'email',
    'rating',
    'shares',
    'device_info'
  ];
  threadData!: any[];
  threadDataSource!: MatTableDataSource<any>;
  @ViewChild('threadTable') threadTable!: MatTable<any[]>;
  @ViewChild('pdfDownloadDialog') pdfDownloadDialog!: TemplateRef<any>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  
  private onComponentDestroy = new Subject();
  perPage: number = 20;
  integrationId!: string;
  integration: any = null;
  includeTests: boolean = false;
  currentPage: number = 0;
  totalItems: number = 0;
  pdfDownloadUrl: string = '';
  userTimezone: string = 'UTC';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private integrationsService: IntegrationsService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private uiService: UiService,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;
    this.integrationId = this.route.snapshot.params['integrationId'];
    this.integration = this.route.snapshot.data.integration;
    this.includeTests = this.parseIncludeTestsQuery(this.route.snapshot.queryParams['include_tests']);
    this.userTimezone = this.sessionService.currentUserValue?.timezone || 'UTC';

    // Use integration ID from resolved data if route param is not available
    if (!this.integrationId && this.integration?.id) {
      this.integrationId = this.integration.id.toString();
    }

    // Get dates from URL parameters or use defaults (last 7 days)
    let startDate: Date, endDate: Date;
    
    const today = moment.tz(this.userTimezone).endOf('day').toDate();
    
    if (this.route.snapshot.queryParams['start_date'] && this.route.snapshot.queryParams['end_date']) {
      const urlStartDate = this.parseDateFromQuery(this.route.snapshot.queryParams['start_date']);
      const urlEndDate = this.parseDateFromQuery(this.route.snapshot.queryParams['end_date']);
      
      // Validate dates are not in the future
      if (urlStartDate && urlEndDate && urlStartDate <= today && urlEndDate <= today) {
        startDate = urlStartDate;
        endDate = urlEndDate;
      } else {
        // Invalid dates in URL, use defaults and clear URL params  
        endDate = moment.tz(this.userTimezone).toDate(); // Today's date
        startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
        
        // Clear invalid URL parameters
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            start_date: this.formatDateForQuery(startDate),
            end_date: this.formatDateForQuery(endDate)
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    } else {
      // Check if we're coming from usage report with dates
      const usageStartDate = this.route.snapshot.queryParams['usage_start_date'];
      const usageEndDate = this.route.snapshot.queryParams['usage_end_date'];
      
      if (usageStartDate && usageEndDate) {
        const usageStart = this.parseDateFromQuery(usageStartDate);
        const usageEnd = this.parseDateFromQuery(usageEndDate);
        
        // Validate dates are not in the future
        if (usageStart && usageEnd && usageStart <= today && usageEnd <= today) {
          startDate = usageStart;
          endDate = usageEnd;
          
          // Convert usage dates to thread dates and update URL
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              start_date: this.formatDateForQuery(startDate),
              end_date: this.formatDateForQuery(endDate)
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        } else {
          // Default to last 7 days in user's timezone (ending today)
          endDate = moment.tz(this.userTimezone).toDate(); // Today's date
          startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
        }
      } else {
        // Default to last 7 days in user's timezone (ending today)
        endDate = moment.tz(this.userTimezone).toDate(); // Today's date
        startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
      }
    }

    // Create validator for dates not in the future
    const maxDateValidator = (control: AbstractControl) => {
      if (!control.value) return null;
      const selectedDate = new Date(control.value);
      selectedDate.setHours(0, 0, 0, 0); // Start of selected day
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      
      return selectedDate > today ? { futureDate: true } : null;
    };

    this.filterForm = new FormGroup({
      startDate: new FormControl(startDate, [Validators.required, maxDateValidator]),
      endDate: new FormControl(endDate, [Validators.required, maxDateValidator]),
    });

    this.threadDataSource = new MatTableDataSource<any>([]);

    // Subscribe to query params changes
    this.route.queryParams
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(params => {
        const includeTestsFromUrl = this.parseIncludeTestsQuery(params['include_tests']);
        const includeTestsChanged = includeTestsFromUrl !== this.includeTests;
        if (includeTestsChanged) {
          this.includeTests = includeTestsFromUrl;
        }

        let shouldReload = includeTestsChanged;
        if (params['start_date'] && params['end_date']) {
          const urlStartDate = this.parseDateFromQuery(params['start_date']);
          const urlEndDate = this.parseDateFromQuery(params['end_date']);
          
          // Only update if dates are different to avoid infinite loop
          if (
            urlStartDate && urlEndDate &&
            (
              urlStartDate.getTime() !== this.filterForm.controls.startDate.value?.getTime() ||
              urlEndDate.getTime() !== this.filterForm.controls.endDate.value?.getTime()
            )
          ) {
            this.filterForm.setValue({
              startDate: urlStartDate,
              endDate: urlEndDate
            }, { emitEvent: false });
            shouldReload = true;
          }
        }
        if (shouldReload) {
          this.currentPage = 0;
          this.loadThreadList();
        }
      });

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.onFilterFormChange(change);
      });

    // Load initial data
    if (this.integrationId) {
      this.loadThreadList();
    } else {
      this.notificationService.openErrorSnackBar('Integration ID not found');
    }
  }

  ngAfterViewInit() {
    this.threadDataSource.paginator = this.paginator;
    this.threadDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  formatDate = (date: Date) => {
    return moment(date).format("MMM DD YYYY, h:mm A");
  }

  formatUserName(firstName: string, lastName: string): string {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return 'Anonymous';
    }
  }

  formatRating(rating: number): string {
    if (!rating) return 'No rating';
    return `${rating}/5`;
  }

  formatDeviceInfo(row: any): string {
    const system = row.system_name || 'Unknown OS';
    const version = row.system_version || '';
    const make = row.make || 'Unknown';
    const model = row.model || '';
    
    let deviceString = '';
    
    if (make === 'web') {
      deviceString = `${system} ${version} - ${model}`;
    } else {
      deviceString = `${system} ${version} - ${make} ${model}`;
    }
    
    return deviceString.trim();
  }

  onFilterFormChange(change: any) {
    // Only proceed if form is valid (no future dates)
    if (!this.filterForm.valid) {
      return;
    }
    
    // Reset to first page when filters change
    this.currentPage = 0;
    
    // Update URL with new date parameters
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        start_date: this.formatDateForQuery(change.startDate),
        end_date: this.formatDateForQuery(change.endDate),
        include_tests: this.includeTests
      },
      queryParamsHandling: 'merge'
    });
    
    this.loadThreadList();
  }

  onIncludeTestsToggle(checked: boolean): void {
    if (this.includeTests === checked) {
      return;
    }
    this.includeTests = checked;
    this.currentPage = 0;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        include_tests: this.includeTests
      },
      queryParamsHandling: 'merge'
    });
    this.loadThreadList();
  }

  loadThreadList() {
    if (!this.integrationId) {
      this.notificationService.openErrorSnackBar('Integration ID is required');
      return;
    }

    // Convert selected dates to UTC for API call
    const selectedStartDate = this.filterForm.controls.startDate.value;
    const selectedEndDate = this.filterForm.controls.endDate.value;
    
    // Create dates using simple ISO date strings for the calendar dates
    // This ensures we get the exact calendar days regardless of timezone
    const startDateStr = selectedStartDate.getFullYear() + '-' + 
                        String(selectedStartDate.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(selectedStartDate.getDate()).padStart(2, '0');
    const endDateStr = selectedEndDate.getFullYear() + '-' + 
                      String(selectedEndDate.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(selectedEndDate.getDate()).padStart(2, '0');
    
    const startDate = startDateStr + 'T00:00:00.000Z';
    const endDate = endDateStr + 'T23:59:59.999Z';

    this.uiService
      .waitForEvent(
        this.integrationsService.getThreadList(
          this.sessionService.currentAccountValue!.accountID,
          +this.integrationId,
          {
            start_date: startDate,
            end_date: endDate,
            include_tests: this.includeTests,
            page: this.currentPage,
            perPage: this.perPage
          }
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.threadData = res.data || [];
          this.totalItems = res.total || 0;
          this.threadDataSource = new MatTableDataSource(this.threadData);
          this.threadDataSource.paginator = this.paginator;
          this.threadDataSource.sort = this.sort;
        },
        () => {
          this.notificationService.openErrorSnackBar(
            'Error retrieving thread list'
          );
        }
      );
  }

  onPageChange(e: PageEvent) {
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
    this.currentPage = e.pageIndex;
    this.loadThreadList();
  }

  onRowClick(row: any) {
    this.router.navigate(['/assistist', this.route.snapshot.params['featureType'], this.integrationId, 'thread', 'detail', row.id]);
  }

  getTotalThreads(): number {
    return this.threadData?.length || 0;
  }

  getTotalWithFeedback(): number {
    return this.threadData?.reduce((total, item) => total + (item.feedback ? 1 : 0), 0) || 0;
  }

  getTotalRatings(): number {
    return this.threadData?.reduce((total, item) => total + (item.rating ? 1 : 0), 0) || 0;
  }

  getTotalShares(): number {
    return this.threadData?.reduce((total, item) => total + (Number(item.shares) || 0), 0) || 0;
  }

  navigateToUsage() {
    const startDate = this.filterForm.controls.startDate.value;
    const endDate = this.filterForm.controls.endDate.value;
    
    this.router.navigate(['/assistist', this.route.snapshot.params['featureType'], this.integrationId, 'usage-report'], { 
      queryParams: {
        thread_start_date: this.formatDateForQuery(startDate),
        thread_end_date: this.formatDateForQuery(endDate),
        include_tests: this.includeTests
      }
    });
  }

  exportToPdf() {
    if (!this.integrationId) {
      this.notificationService.openErrorSnackBar('Integration ID is required');
      return;
    }

    const { startDate, endDate } = this.getDateRangeQuery();
    this.uiService
      .waitForEvent(
        this.integrationsService.exportThreadListPdf(
          this.sessionService.currentAccountValue!.accountID,
          +this.integrationId,
          {
            start_date: startDate,
            end_date: endDate,
            include_tests: this.includeTests
          }
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res: any) => {
          if (res?.url) {
            this.pdfDownloadUrl = res.url;
            this.dialog.open(this.pdfDownloadDialog, {
              width: '520px'
            });
            return;
          }
          this.notificationService.openErrorSnackBar('PDF export did not return a file URL');
        },
        () => {
          this.notificationService.openErrorSnackBar('Error exporting thread PDF');
        }
      );
  }

  private getDateRangeQuery(): { startDate: string; endDate: string } {
    const selectedStartDate = this.filterForm.controls.startDate.value;
    const selectedEndDate = this.filterForm.controls.endDate.value;

    const startDateStr = selectedStartDate.getFullYear() + '-' +
      String(selectedStartDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(selectedStartDate.getDate()).padStart(2, '0');
    const endDateStr = selectedEndDate.getFullYear() + '-' +
      String(selectedEndDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(selectedEndDate.getDate()).padStart(2, '0');

    return {
      startDate: startDateStr + 'T00:00:00.000Z',
      endDate: endDateStr + 'T23:59:59.999Z'
    };
  }

  private parseDateFromQuery(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }

    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private formatDateForQuery(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }

  private parseIncludeTestsQuery(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase());
    }
    return false;
  }
}
